import React, { useEffect, useState } from "react";
import { Refine } from "@refinedev/core";
import { notificationProvider } from "@refinedev/antd";
import { typo3DataProvider } from "./providers/dataProvider";
import routerBindings, {
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { HashRouter, Outlet, Route, Routes } from "react-router-dom";
import { ConfigProvider, App as AntdApp, Spin, Layout } from "antd";
import deDE from "antd/locale/de_DE";
import "@refinedev/antd/dist/reset.css";
import "./App.css";


// Auth Components
import { useAuth } from "./providers/authProvider";
import { LoginModal } from "./components";

// API Debug
import { debugApiConfig } from "./config/api";

// Ressourcen
import { InvoiceList, InvoiceCreate, InvoiceEdit, InvoiceShow } from "./pages/invoices";
import { SenderList, AdvertDetail, AdvertCreate } from "./pages/senders";
import { TemplateList } from "./pages/templates";
import { GuestList, GuestCreate, GuestEdit, GuestShow } from "./pages/guests";
import { BookingTimeline } from "./pages/bookings";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { GuestCommunication } from "./pages/gaestekommunikation";
import { GuidedOnepageBooking } from "./pages/gopb";
import { PricingList } from "./pages/pricing/list";
import { TaskListList, TaskListCreate, TaskListEdit, TaskListShow } from "./pages/tasklists";
import { MobileTaskListWrapper } from "./pages/tasklists/mobile/MobileTaskListWrapper";

// Custom Components
import { Header } from "./components/Header";
import { CustomSider } from "./components/CustomSider";

// Icons
import {
  DashboardOutlined,
  FileTextOutlined,
  UserOutlined,
  FileOutlined,
  TeamOutlined,
  BookOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CalendarOutlined,
  MessageOutlined,
  HomeOutlined,
  FileImageOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

// Platzhalter-Komponenten für neue Funktionen
import { GuestmapEditor } from "./pages/gaestemappe/GuestmapEditor";
import { ActiveGuestmapsList } from "./pages/gaestemappe/ActiveGuestmapsList";
import { GuestmapTemplates } from "./pages/gaestemappe/GuestmapTemplates";
import { MediaManager } from "./pages/media/MediaManager";
import { PriceSettingsPage } from "./pages/preiseinstellungen/PriceSettingsPage";
import { Channelmanager } from "./pages/channelmanager/Channelmanager";
import { ArrivalDepartureList } from "./pages/arrival-departure";

function App() {
  const { isAuthenticated, isInitialized, checkSession } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    // Session-Check beim App-Start
    const initializeAuth = async () => {
      console.log('🔧 Starte Session-Check...');
      
      const result = await checkSession();
      
      if (!result.isAuthenticated) {
        console.log('❌ Keine gültige Session gefunden - zeige Login-Modal');
        setShowLoginModal(true);
      } else {
        console.log('✅ Gültige Session gefunden:', result.user);
        setShowLoginModal(false);
      }
    };

    initializeAuth();
  }, []);



  // Zeige Loading Spinner während der Session-Überprüfung
  if (!isInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#666' }}>
          Buchungsmanager wird geladen...
        </div>
      </div>
    );
  }

  return (
    <HashRouter>
      <ConfigProvider 
        locale={deDE}
      >
        <AntdApp>
          <Refine
            dataProvider={typo3DataProvider}
            notificationProvider={notificationProvider}
            routerProvider={routerBindings}
            resources={[
              {
                name: "dashboard",
                list: "/dashboard",
                meta: {
                  label: "Dashboard",
                  icon: <DashboardOutlined />,
                },
              },
              {
                name: "invoices",
                list: "/rechnungen",
                create: "/rechnungen/neu",
                edit: "/rechnungen/bearbeiten/:id",
                show: "/rechnungen/anzeigen/:id",
                meta: {
                  label: "Rechnungen (Legacy)",
                  icon: <FileTextOutlined />,
                },
              },
              {
                name: "bookings",
                list: "/buchungen",
                meta: {
                  label: "Buchungskalender",
                  icon: <CalendarOutlined />,
                },
              },
              {
                name: "digitale-gaestemappe",
                list: "/digitale-gaestemappe",
                create: "/digitale-gaestemappe",
                edit: "/digitale-gaestemappe/bearbeiten/:id",
                meta: {
                  label: "Digitale Gästemappe",
                  icon: <BookOutlined />,
                },
              },
              {
                name: "gaestemappen-vorlagen",
                list: "/gaestemappen-vorlagen",
                meta: {
                  label: "Gästemappe Vorlagen",
                  icon: <FileOutlined />,
                },
              },
              {
                name: "guests",
                list: "/gaeste",
                create: "/gaeste/neu",
                edit: "/gaeste/bearbeiten/:id",
                show: "/gaeste/anzeigen/:id",
                meta: {
                  label: "Gästedaten",
                  icon: <TeamOutlined />,
                },
              },
              {
                name: "senders",
                list: "/objekte",
                meta: {
                  label: "Objekte / Inserate",
                  icon: <HomeOutlined />,
                },
              },

              {
                name: "digital-guestmap",
                list: "/digitale-gaestemappe",
                meta: {
                  label: "Digitale Gästemappe",
                  icon: <BookOutlined />,
                },
              },
              {
                name: "active-guestmaps",
                list: "/aktive-gaestemappen",
                meta: {
                  label: "Aktive Gästemappen",
                  icon: <UserOutlined />,
                },
              },
              {
                name: "guestmap-templates",
                list: "/gaestemappen-vorlagen",
                meta: {
                  label: "Gästemappen-Vorlagen",
                  icon: <FileOutlined />,
                },
              },
              {
                name: "guided-booking",
                list: "/guided-onepage-booking",
                meta: {
                  label: "Guided OnePage Booking",
                  icon: <ShoppingCartOutlined />,
                },
              },
              {
                name: "pricing",
                list: "/preis-einstellungen",
                meta: {
                  label: "Preis Einstellungen",
                  icon: <DollarOutlined />,
                },
              },
              {
                name: "arrival-departure",
                list: "/an-abreise",
                meta: {
                  label: "An & Abreise",
                  icon: <CalendarOutlined />,
                },
              },
              {
                name: "guest-communication",
                list: "/gaestekommunikation",
                meta: {
                  label: "Kommunikation",
                  icon: <MessageOutlined />,
                },
              },
              {
                name: "templates",
                list: "/textbausteine",
                meta: {
                  label: "Textbausteine",
                  icon: <FileOutlined />,
                },
              },
              {
                name: "media-manager",
                list: "/medien-bilder",
                meta: {
                  label: "Medien & Bilder",
                  icon: <FileImageOutlined />,
                },
              },
              {
                name: "tasklists",
                list: "/aufgabenlisten",
                create: "/aufgabenlisten/neu",
                edit: "/aufgabenlisten/bearbeiten/:id",
                show: "/aufgabenlisten/anzeigen/:id",
                meta: {
                  label: "Aufgabenlisten",
                  icon: <UnorderedListOutlined />,
                },
              },
              {
                name: "arrival-departure-rules",
                list: "/an-abreise",
                meta: {
                  label: "An & Abreise",
                  icon: <CalendarOutlined />,
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              projectId: "invoice-manager",
            }}
          >
            <Routes>
              <Route
                element={
                  <Layout style={{ minHeight: '100vh' }}>
                    <CustomSider />
                    <Layout>
                      <Header />
                      <Layout.Content style={{ padding: '24px', background: '#f0f2f5' }}>
                        <Outlet />
                      </Layout.Content>
                    </Layout>
                  </Layout>
                }
              >
                <Route index element={<NavigateToResource resource="dashboard" />} />
                
                {/* Dashboard */}
                <Route path="/dashboard">
                  <Route index element={<Dashboard />} />
                </Route>

                {/* Rechnungen */}
                <Route path="/rechnungen">
                  <Route index element={<InvoiceList />} />
                  <Route path="neu" element={<InvoiceCreate />} />
                  <Route path="bearbeiten/:id" element={<InvoiceEdit />} />
                  <Route path="anzeigen/:id" element={<InvoiceShow />} />
                </Route>

                {/* Buchungskalender */}
                <Route path="/buchungen">
                  <Route index element={<BookingTimeline />} />
                </Route>

                {/* Gästedaten */}
                <Route path="/gaeste">
                  <Route index element={<GuestList />} />
                  <Route path="neu" element={<GuestCreate />} />
                  <Route path="bearbeiten/:id" element={<GuestEdit />} />
                  <Route path="anzeigen/:id" element={<GuestShow />} />
                </Route>

                {/* Objekte / Inserate */}
                <Route path="/objekte">
                  <Route index element={<SenderList />} />
                  <Route path="neu" element={<AdvertCreate />} />
                </Route>

                {/* Objekte Detail */}
                <Route path="/objekte/:id" element={<AdvertDetail />} />

                {/* Textbausteine */}
                <Route path="/textbausteine">
                  <Route index element={<TemplateList />} />
                </Route>

                {/* Neue Funktionen */}
                <Route path="/digitale-gaestemappe">
                  <Route index element={<GuestmapEditor />} />
                  <Route path="bearbeiten/:id" element={<GuestmapEditor />} />
                </Route>

                <Route path="/aktive-gaestemappen">
                  <Route index element={<ActiveGuestmapsList />} />
                </Route>

                <Route path="/gaestemappen-vorlagen">
                  <Route index element={<GuestmapTemplates />} />
                </Route>

                <Route path="/guided-onepage-booking">
                  <Route index element={<GuidedOnepageBooking />} />
                </Route>

                <Route path="/preis-einstellungen">
                  <Route index element={<PriceSettingsPage />} />
                </Route>

                <Route path="/an-abreise">
                  <Route index element={<ArrivalDepartureList />} />
                </Route>

                <Route path="/gaestekommunikation">
                  <Route index element={<GuestCommunication />} />
                </Route>

                <Route path="/medien-bilder">
                  <Route index element={<MediaManager />} />
                </Route>

                <Route path="/channelmanager">
                  <Route index element={<Channelmanager />} />
                </Route>

                <Route path="/aufgabenlisten">
                  <Route index element={<TaskListList />} />
                  <Route path="neu" element={<TaskListCreate />} />
                  <Route path="bearbeiten/:id" element={<TaskListEdit />} />
                  <Route path="anzeigen/:id" element={<TaskListShow />} />
                </Route>

                <Route path="/mobile/tasklist/:token" element={<MobileTaskListWrapper />} />

                <Route path="*" element={<div>404 - Seite nicht gefunden</div>} />
              </Route>
            </Routes>
            <UnsavedChangesNotifier />
          </Refine>
        </AntdApp>
        
        {/* Login Modal */}
        <LoginModal
          visible={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />
      </ConfigProvider>
    </HashRouter>
  );
}

export default App; 